import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Mail,
  MenuIcon,
  X,
  LogIn,
  LogOut,
  MessageSquare,
  Brain,
  Download,
  BookOpenCheck,
  Users,
  Phone,
  MessageCircle
} from 'lucide-react';
import { supabase } from './utils/supabase';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AdminDashboard from './pages/AdminDashboard';
import AuthModal from './components/AuthModal';
import PricingButton from './components/PricingButton';
import ThankYou from './pages/ThankYou';
import CheckoutCancel from './pages/CheckoutCancel';
import Checkout from './pages/Checkout';
import Dashboard from './pages/Dashboard';
import LearningHub from './pages/LearningHub';
import TutorHub from './pages/TutorHub';

function App() {
  const [activeTab, setActiveTab] = useState('welcome');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    window.addEventListener('openAuthModal', () => setIsAuthModalOpen(true));

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('openAuthModal', () => setIsAuthModalOpen(true));
    };
  }, []);

  useEffect(() => {
    // Check if user is trying to access protected routes
    const protectedRoutes = ['/dashboard', '/learning-hub', '/tutor-hub'];
    const currentPath = window.location.pathname;
    
    if (protectedRoutes.includes(currentPath) && !user) {
      navigate('/?signin=true');
    }
  }, [user, navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveTab(sectionId);
    setIsMenuOpen(false);
  };

  const navigationItems = ['welcome', 'how-it-works', 'bonuses', 'pricing', 'faq', 'contact'];

  return (
    <>
      <Routes>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/checkout-cancel" element={<CheckoutCancel />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route 
          path="/dashboard" 
          element={
            user ? <Dashboard /> : <Navigate to="/?signin=true" replace />
          }
        />
        <Route 
          path="/learning-hub" 
          element={
            user ? <LearningHub /> : <Navigate to="/?signin=true" replace />
          }
        />
        <Route 
          path="/tutor-hub" 
          element={
            user ? <TutorHub /> : <Navigate to="/?signin=true" replace />
          }
        />
        <Route path="/" element={
          <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            {/* Navigation */}
            <nav className="fixed w-full bg-white/80 backdrop-blur-sm shadow-sm z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                  <div className="flex items-center">
                    <BookOpen className="h-8 w-8 text-indigo-600" />
                    <span className="ml-2 text-xl font-bold text-gray-900">AI Learning Hub</span>
                  </div>
                  
                  <div className="hidden md:flex items-center space-x-8">
                    {navigationItems.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => scrollToSection(tab)}
                        className={`${
                          activeTab === tab
                            ? 'text-indigo-600 border-b-2 border-indigo-600'
                            : 'text-gray-500 hover:text-gray-900'
                        } px-1 py-2 text-sm font-medium capitalize transition-colors`}
                      >
                        {tab.replace(/-/g, ' ')}
                      </button>
                    ))}
                    {user ? (
                      <button
                        onClick={handleLogout}
                        className="flex items-center text-gray-500 hover:text-gray-900"
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Sign Out
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsAuthModalOpen(true)}
                        className="flex items-center text-gray-500 hover:text-gray-900"
                      >
                        <LogIn className="w-4 h-4 mr-1" />
                        Sign In
                      </button>
                    )}
                  </div>

                  <div className="md:hidden flex items-center">
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="text-gray-500 hover:text-gray-900"
                    >
                      {isMenuOpen ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                    </button>
                  </div>
                </div>
              </div>

              {isMenuOpen && (
                <div className="md:hidden">
                  <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg">
                    {navigationItems.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => scrollToSection(tab)}
                        className={`${
                          activeTab === tab
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-500 hover:bg-gray-50'
                        } block px-3 py-2 rounded-md text-base font-medium capitalize w-full text-left`}
                      >
                        {tab.replace(/-/g, ' ')}
                      </button>
                    ))}
                    {user ? (
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-3 py-2 text-gray-500 hover:bg-gray-50"
                      >
                        <LogOut className="w-4 h-4 mr-1" />
                        Sign Out
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsAuthModalOpen(true)}
                        className="flex items-center w-full px-3 py-2 text-gray-500 hover:bg-gray-50"
                      >
                        <LogIn className="w-4 h-4 mr-1" />
                        Sign In
                      </button>
                    )}
                  </div>
                </div>
              )}
            </nav>

            {/* Welcome Section */}
            <section id="welcome" className="pt-24 pb-16 md:pt-32 md:pb-24 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center">
                  <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                    Master AI with Interactive Learning & Expert Tutoring
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                    Get unlimited access to our comprehensive AI learning platform and personalized AI tutoring - all in one subscription.
                  </p>
                  <button
                    onClick={() => scrollToSection('pricing')}
                    className="bg-indigo-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all shadow-lg"
                  >
                    Start Learning Today
                  </button>
                </div>
              </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="py-16 bg-white px-4">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900">
                  Two Powerful Learning Tools, One Platform
                </h2>
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-2xl">
                    <div className="flex items-center mb-6">
                      <Brain className="h-12 w-12 text-indigo-600" />
                      <h3 className="text-2xl font-bold ml-4 text-gray-900">AI Learning Hub</h3>
                    </div>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                        <span>30+ Interactive AI Topics & Courses</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                        <span>Hands-on Projects & Challenges</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                        <span>Progress Tracking & Certificates</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl">
                    <div className="flex items-center mb-6">
                      <MessageSquare className="h-12 w-12 text-purple-600" />
                      <h3 className="text-2xl font-bold ml-4 text-gray-900">AI Tutor Hub</h3>
                    </div>
                    <ul className="space-y-4">
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                        <span>Personalized AI Tutoring Sessions</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                        <span>Real-time Problem Solving</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle2 className="h-6 w-6 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                        <span>Instant Help & Guidance</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Bonuses Section */}
            <section id="bonuses" className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50 px-4">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900">
                  Exclusive AI Training Bonuses
                </h2>
                
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="bg-white rounded-2xl p-8 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="flex items-center justify-center mb-6">
                      <Download className="w-12 h-12 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">FREE AI Side Hustle Ebook</h3>
                    <p className="text-gray-600 mb-6">Discover how to make money with AI in this comprehensive step-by-step guide.</p>
                    <a
                      href="https://ebook.aichaperones.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors"
                    >
                      <span>📥 Download Now</span>
                    </a>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="flex items-center justify-center mb-6">
                      <BookOpenCheck className="w-12 h-12 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">AI Side Hustle Course</h3>
                    <p className="text-gray-600 mb-6">Get hands-on training to launch your AI side hustle today with practical exercises.</p>
                    <a
                      href="https://aihustle.aichaperones.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors"
                    >
                      <span>📚 Start the Course</span>
                    </a>
                  </div>

                  <div className="bg-white rounded-2xl p-8 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="flex items-center justify-center mb-6">
                      <Users className="w-12 h-12 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">FREE AI Community</h3>
                    <p className="text-gray-600 mb-6">Join our thriving community of AI enthusiasts. Learn, network, and grow together.</p>
                    <a
                      href="https://aichaperones.com/community/space/start-here/home"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors"
                    >
                      <span>🔗 Join Now</span>
                    </a>
                  </div>
                </div>

                <div className="mt-12 text-center">
                  <p className="text-gray-600">
                    All bonuses are included free with your subscription.
                    <br />
                    Start learning today and get instant access to these exclusive resources!
                  </p>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-16 bg-gray-50 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                    One Subscription, Complete AI Education
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Get unlimited access to both our AI Learning Hub and AI Tutor Hub with a single subscription.
                  </p>
                  {!user && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
                      <p className="text-blue-800">
                        Please <button 
                          onClick={() => setIsAuthModalOpen(true)}
                          className="font-semibold text-blue-600 hover:text-blue-800 underline"
                        >
                          sign in or create an account
                        </button> to subscribe to our services.
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Monthly Access</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-gray-900">$49</span>
                      <span className="text-gray-600">/month</span>
                    </div>
                    <div className="mb-8">
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Full Access to AI Learning Hub</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Unlimited AI Tutoring Sessions</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Community Support</span>
                        </li>
                      </ul>
                    </div>
                    <PricingButton
                      onSignInClick={() => setIsAuthModalOpen(true)}
                      isAuthenticated={!!user}
                      interval="monthly"
                    />
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-indigo-600 relative">
                    <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Best Value
                    </span>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Yearly Access</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-gray-900">$441</span>
                      <span className="text-gray-600">/year</span>
                      <span className="ml-2 text-indigo-600 font-semibold">Save 25%</span>
                    </div>
                    <div className="mb-8">
                      <ul className="space-y-4">
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Everything in Monthly Plan</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Priority Support</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="h-5 w-5 text-indigo-600 mr-3 mt-0.5" />
                          <span>Advanced Features</span>
                        </li>
                      </ul>
                    </div>
                    <PricingButton
                      onSignInClick={() => setIsAuthModalOpen(true)}
                      isAuthenticated={!!user}
                      interval="yearly"
                    />
                  </div>
                </div>

                <div className="mt-12 text-center">
                  <p className="text-gray-600">
                    All plans include both AI Learning Hub and AI Tutor Hub access.
                    <br />
                    No hidden fees. Cancel anytime.
                  </p>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="py-16 bg-white px-4">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900">
                  Frequently Asked Questions
                </h2>
                <div className="max-w-3xl mx-auto space-y-6">
                  {[
                    {
                      question: "What's included in the subscription?",
                      answer: "Your subscription includes unlimited access to both our AI Learning Hub with 30+ interactive courses and our AI Tutor Hub for personalized learning support. You'll get access to all learning materials, projects, tutoring sessions, and regular updates.",
                    },
                    {
                      question: "How does the AI Tutor Hub work?",
                      answer: "The AI Tutor Hub provides personalized tutoring sessions where you can ask questions, get help with projects, and receive instant guidance. Our AI tutors are available 24/7 and can assist with any topic covered in our curriculum.",
                    },
                    {
                      question: "Can I switch between plans?",
                      answer: "Yes, you can upgrade to the annual plan at any time to save 25%. If you need to change your subscription, you can do so from your account settings.",
                    },
                    {
                      question: "Is there a limit to tutoring sessions?",
                      answer: "No, both plans include unlimited access to AI tutoring sessions. You can use the tutoring service as often as you need.",
                    },
                  ].map((faq, index) => (
                    <details
                      key={index}
                      className="group bg-gray-50 rounded-lg p-6 [&_summary::-webkit-details-marker]:hidden"
                    >
                      <summary className="flex items-center justify-between cursor-pointer">
                        <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                        <ChevronDown className="h-5 w-5 text-gray-500 group-open:rotate-180 transition-transform" />
                      </summary>
                      <p className="mt-4 text-gray-600 leading-relaxed">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-16 bg-gray-50 px-4">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900">
                  Contact Us
                </h2>
                <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
                  <div className="space-y-8">
                    <div className="flex items-center space-x-4">
                      <Mail className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                        <a href="mailto:contact@aichaperones.com" className="text-indigo-600 hover:text-indigo-800">
                          contact@aichaperones.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <Phone className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Phone</h3>
                        <a href="tel:800-514-6179" className="text-indigo-600 hover:text-indigo-800">
                          800-514-6179
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <MessageCircle className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Text</h3>
                        <a href="sms:845-636-3745" className="text-indigo-600 hover:text-indigo-800">
                          845-636-3745
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-center text-gray-600">
                      We're here to help! Choose your preferred method of contact above.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-300 py-12 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center">
                      <BookOpen className="h-8 w-8 text-indigo-400" />
                      <span className="ml-2 text-xl font-bold text-white">AI Learning Hub</span>
                    </div>
                    <p className="mt-4 text-gray-400 max-w-md">
                      Empowering the next generation of AI professionals through interactive learning
                      and expert guidance.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Legal</h3>
                      <ul className="space-y-2">
                        <li>
                          <Link to="/privacy" className="hover:text-white transition-colors">
                            Privacy Policy
                          </Link>
                        </li>
                        <li>
                          <Link to="/terms" className="hover:text-white transition-colors">
                            Terms of Service
                          </Link>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Support</h3>
                      <ul className="space-y-2">
                        <li>
                          <a href="#faq" className="hover:text-white transition-colors">
                            FAQ
                          </a>
                        </li>
                        <li>
                          <a href="#contact" className="hover:text-white transition-colors">
                            Contact
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="mt-12 pt-8 border-t border-gray-800 text-center">
                  <p>© 2025 AI Learning Hub. All rights reserved.</p>
                </div>
              </div>
            </footer>
          </div>
        } />
      </Routes>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </>
  );
}

export default App;